import { ClinicAuthError } from "@auth/clinicAuthClient";
import { useMintPairingCode } from "@connector/connectorQueries";
import { useCallback, useEffect, useState } from "react";

export interface UsePairingCodeResult {
  /** The active pairing code, or null before the first mint. */
  code: string | null;
  /** Seconds remaining before the active code expires (0 when none/expired). */
  secondsLeft: number;
  /** A mint request is in flight. */
  loading: boolean;
  /** Friendly error message from the last mint attempt, if any. */
  error: string | null;
  /** True once a minted code has lapsed (re-enable "Generate"). */
  expired: boolean;
  /** Mint a fresh pairing code and (re)start the countdown. */
  generate: (name?: string) => void;
}

const messageFor = (err: unknown): string => {
  if (err instanceof ClinicAuthError) {
    if (err.code === "network_error")
      return "Couldn't reach the server. Check your connection and try again.";
    if (err.code === "invalid_credentials")
      return "Your session expired. Sign in again to generate a code.";
    if (err.code === "rate_limited")
      return "Too many attempts. Wait a moment and try again.";
  }
  return "Something went wrong generating a code. Try again.";
};

export const usePairingCode = (): UsePairingCodeResult => {
  const { mutate, isPending, isError, error } = useMintPairingCode();
  const [code, setCode] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);

  const expired = code !== null && secondsLeft <= 0;

  // Drive the countdown off a wall-clock deadline so a backgrounded tab can't
  // drift — each tick recomputes from `expiresAt` rather than decrementing a
  // local integer. `generate()` seeds the initial `secondsLeft`, so the timer
  // (an external subscription) is the only thing that updates state here.
  useEffect(() => {
    if (expiresAt === null) return;
    const id = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const generate = useCallback(
    (name?: string) => {
      mutate(name, {
        onSuccess: ({ code: next, ttlSeconds }) => {
          setCode(next);
          setSecondsLeft(ttlSeconds);
          setExpiresAt(Date.now() + ttlSeconds * 1000);
        },
        onError: () => {
          setCode(null);
          setExpiresAt(null);
          setSecondsLeft(0);
        },
      });
    },
    [mutate],
  );

  return {
    code,
    secondsLeft,
    loading: isPending,
    error: isError ? messageFor(error) : null,
    expired,
    generate,
  };
};
